import React, { useState, useCallback, useMemo } from 'react';
import { FeedsDataItem } from '../feeds_list_types';
import feedsRepo from '../../data/feedsRepo';
import visitsRepo from '../../data/visitsRepo';
import Dialog from '../../../../core/components/Dialog';
import Button from '../../../../core/components/Button';
import { ngDelegate } from '../../../../core/services/ng.delegate';
import { useAtomValue } from 'jotai';
import { feedsUserContextAtom } from '../feeds_list_atoms';

interface FeedActionButtonsProps {
    feedItem: FeedsDataItem;
    onRefreshFeedData?: (feedId: string) => Promise<void>;
    onCloseFlyoutAndRefreshTable?: () => void;
}

type ConfirmationDialog = {
    show: boolean;
    title: string;
    message: string;
    action: () => Promise<void>;
    successMessage: string;
    hasNotes?: boolean;
    notes?: string;
    unlockForReload?: boolean;
};

const FeedActionButtons = ({
    feedItem,
    onRefreshFeedData,
    onCloseFlyoutAndRefreshTable,
}: FeedActionButtonsProps): JSX.Element | null => {
    const [isLoading, setIsLoading] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState<ConfirmationDialog>({
        show: false,
        title: '',
        message: '',
        action: async () => {},
        successMessage: '',
    });
    const [notes, setNotes] = useState('');

    // Get user context from atom (set once at page init)
    const { userType } = useAtomValue(feedsUserContextAtom);
    const isVeritySA = userType === 'VERITYSA';

    // Determine which buttons to show based on Angular logic

    const showSkippedButtons = feedItem.ObjectState === 'SKIPPED';

    const showReverseButton =
        !showSkippedButtons &&
        isVeritySA &&
        (feedItem.FeedType === 'SPLITTRANS' ||
            feedItem.FeedType === 'PATIENTVISITS' ||
            feedItem.FeedType === 'EPRESCRIBE') &&
        feedItem.ObjectState !== 'REVERSED' &&
        feedItem.ObjectState !== 'QUEUED';

    const showReloadButton =
        !showSkippedButtons &&
        !showReverseButton &&
        isVeritySA &&
        feedItem.RecordsProcessed < 1 &&
        (feedItem.ObjectState === 'WARNING' ||
            feedItem.ObjectState === 'ERROR' ||
            feedItem.ObjectState === 'PROCESSED');

    // Generic action handler
    const handleAction = useCallback(
        async (dialog: ConfirmationDialog) => {
            if (isLoading) return;

            try {
                console.debug('[FeedActionButtons] handleAction start', dialog.title);
                setIsLoading(true);
                ngDelegate.wait(true);

                await dialog.action();
                console.debug('[FeedActionButtons] dialog.action resolved', dialog.title);

                // Add delay to allow backend to update ObjectState (to match Angular timing)
                await new Promise(res => setTimeout(res, 900));

                ngDelegate.wait(false);
                setIsLoading(false);
                setConfirmDialog({ ...dialog, show: false });

                // Show success message
                ngDelegate.ok(dialog.title + ' Success', dialog.successMessage);

                // Always refresh feed data after any action
                if (onRefreshFeedData) {
                    console.debug('[FeedActionButtons] calling onRefreshFeedData', feedItem.Id);
                    await onRefreshFeedData(feedItem.Id);
                }
                // For reload, also close flyout and refresh table if needed
                if (dialog.title === 'Move File for Reload' && onCloseFlyoutAndRefreshTable) {
                    onCloseFlyoutAndRefreshTable();
                }

                // Always clear notes after dialog submission
                setNotes('');

                // Note: No page reload - let the parent component handle data refresh if needed
            } catch (error) {
                ngDelegate.wait(false);
                setIsLoading(false);
                console.error('[FeedActionButtons] handleAction error', dialog.title, error);
                ngDelegate.error(
                    'Unexpected error',
                    dialog.title,
                    '',
                    error instanceof Error ? error.message : 'Unknown error',
                );
            }
        },
        [isLoading, onRefreshFeedData, feedItem.Id, onCloseFlyoutAndRefreshTable],
    );

    // Button handlers
    const handleProcessFile = useCallback(() => {
        setConfirmDialog({
            show: true,
            title: 'Process File',
            message: 'Process this skipped file?',
            action: async () => feedsRepo.processFile(feedItem.Id),
            successMessage: 'File is no longer in SKIPPED status, it will be processed shortly.',
        });
    }, [feedItem.Id]);

    const handleArchiveFile = useCallback(() => {
        setConfirmDialog({
            show: true,
            title: 'Archive File',
            message: 'Archive this skipped file?',
            action: async () => feedsRepo.archiveFile(feedItem.Id),
            successMessage: 'File is no longer in SKIPPED status, it will be archived shortly.',
        });
    }, [feedItem.Id]);

    const handleReverseFile = useCallback(() => {
        if (
            (feedItem.FeedType === 'PATIENTVISITS' || feedItem.FeedType === 'EPRESCRIBE') &&
            (!feedItem.CoveredEntity || feedItem.CoveredEntity.length === 0)
        ) {
            ngDelegate.ok(
                'File Reverse Not Allowed',
                'At least 1 CEID must be known to reverse a PATIENTVISITS or EPRESCRIBE file.',
            );
            return;
        }

        setConfirmDialog({
            show: true,
            title: 'Reverse File',
            message: 'Reverse all transactions in this file?',
            hasNotes: true,
            action: async () => {
                const latestNotes = document.querySelector('textarea')?.value || '';
                if (feedItem.FeedType === 'PATIENTVISITS' || feedItem.FeedType === 'EPRESCRIBE') {
                    await visitsRepo.reverseVisitMulti({
                        ceid: Array.isArray(feedItem.CoveredEntity)
                            ? feedItem.CoveredEntity[0]
                            : feedItem.CoveredEntity,
                        type: 'FILE',
                        value: feedItem.Id,
                        notes: latestNotes,
                        useDate: false,
                        resetFileHash: false,
                        resetRecordHash: false,
                    });
                    return;
                }
                return feedsRepo.reverseFile({
                    id: feedItem.Id,
                    resetFileHash: false,
                    resetRecordHash: false,
                    notes: latestNotes,
                });
            },
            successMessage: 'File reversal record has been created, it will be processed shortly.',
        });
    }, [feedItem.Id, feedItem.FeedType, feedItem.CoveredEntity]);

    const handleReverseFileReload = useCallback(() => {
        console.debug('[FeedActionButtons] handleReverseFileReload called');
        if (
            (feedItem.FeedType === 'PATIENTVISITS' || feedItem.FeedType === 'EPRESCRIBE') &&
            (!feedItem.CoveredEntity || feedItem.CoveredEntity.length === 0)
        ) {
            ngDelegate.ok(
                'File Reverse Not Allowed',
                'At least 1 CEID must be known to reverse a PATIENTVISITS or EPRESCRIBE file.',
            );
            return;
        }

        setConfirmDialog({
            show: true,
            title: 'Reverse File for Reload',
            message: 'Reverse file and allow reload of the same file in the future?',
            hasNotes: true,
            unlockForReload: true,
            action: async () => {
                console.debug('[FeedActionButtons] handleReverseFileReload dialog.action called');
                const latestNotes = document.querySelector('textarea')?.value || '';
                if (feedItem.FeedType === 'PATIENTVISITS' || feedItem.FeedType === 'EPRESCRIBE') {
                    await visitsRepo.reverseVisitMulti({
                        ceid: Array.isArray(feedItem.CoveredEntity)
                            ? feedItem.CoveredEntity[0]
                            : feedItem.CoveredEntity,
                        type: 'FILE',
                        value: feedItem.Id,
                        notes: latestNotes,
                        useDate: false,
                        resetFileHash: true,
                        resetRecordHash: true,
                    });
                    return;
                }
                return feedsRepo.reverseFile({
                    id: feedItem.Id,
                    resetFileHash: true,
                    resetRecordHash: true,
                    notes: latestNotes,
                });
            },
            successMessage: 'File reversal record has been created, it will be processed shortly.',
        });
    }, [feedItem.Id, feedItem.FeedType, feedItem.CoveredEntity]);

    const handleReloadFile = useCallback(() => {
        setConfirmDialog({
            show: true,
            title: 'Move File for Reload',
            message: 'Move this feed file back to the import folder for reload?',
            action: async () => {
                await feedsRepo.reloadFile(feedItem.Id);
                // Note: flyout closing and table refresh is handled by the generic handleAction function
            },
            successMessage: 'File has been moved back to import folder for reload',
        });
    }, [feedItem.Id]);

    // Dialog actions
    const dialogActions = useMemo(
        () => [
            {
                label: 'OK',
                onClick: (): Promise<void> => {
                    console.debug('[FeedActionButtons] Dialog OK button clicked');
                    return handleAction(confirmDialog);
                },
                cssName: 'btn-alt',
            },
            {
                label: 'CANCEL',
                onClick: (): void => setConfirmDialog({ ...confirmDialog, show: false }),
                cssName: 'btn-alt',
            },
        ],
        [confirmDialog, handleAction],
    );

    // Don't render anything if no buttons should be shown
    if (!showSkippedButtons && !showReverseButton && !showReloadButton) {
        return null;
    }

    return (
        <>
            {/* Action Buttons */}
            <div className="action-buttons">
                {showSkippedButtons && (
                    <div className="button-group">
                        <div className="info-tooltip">
                            <span className="tooltip-text">
                                PROCESS FILE adds file back to regular file processing. ARCHIVE FILE stores file without
                                any processing.
                            </span>
                        </div>
                        <Button
                            cssName="btn-alt"
                            onClick={handleProcessFile}
                            disabled={isLoading}
                        >
                            PROCESS FILE
                        </Button>
                        <Button
                            cssName="btn-alt"
                            onClick={handleArchiveFile}
                            disabled={isLoading}
                        >
                            ARCHIVE FILE
                        </Button>
                    </div>
                )}

                {showReverseButton && (
                    <>
                        <div className="button-group">
                            <div className="info-tooltip">
                                <span className="tooltip-text">
                                    {`Choose the "REVERSE FILE FOR RELOAD" option if you need to re-load the same
                                    file in the future.`}
                                </span>
                            </div>
                        </div>
                        <div className="button-group">
                            <Button
                                cssName="btn-alt"
                                onClick={handleReverseFile}
                                disabled={isLoading}
                            >
                                REVERSE FILE
                            </Button>
                            <Button
                                cssName="btn-alt"
                                onClick={() => {
                                    console.debug('[FeedActionButtons] REVERSE FILE FOR RELOAD button clicked');
                                    handleReverseFileReload();
                                }}
                                disabled={isLoading}
                            >
                                REVERSE FILE FOR RELOAD
                            </Button>
                        </div>
                    </>
                )}

                {showReloadButton && (
                    <div className="button-group">
                        <div className="info-tooltip">
                            <span className="tooltip-text">
                                This option will move the import file back to import folder for a full reload.
                            </span>
                        </div>
                        <Button
                            cssName="btn-alt"
                            onClick={handleReloadFile}
                            disabled={isLoading}
                        >
                            MOVE FILE FOR RELOAD
                        </Button>
                    </div>
                )}
            </div>

            {/* Confirmation Dialog */}
            {confirmDialog.show && (
                <Dialog
                    open={confirmDialog.show}
                    setOpen={open => setConfirmDialog({ ...confirmDialog, show: open })}
                    title={confirmDialog.title}
                    actions={dialogActions}
                    // Custom message content: message + notes input
                    message={confirmDialog.message}
                >
                    <div style={{ marginTop: '10px', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
                        <label>Optional notes (max 100 characters):</label>
                        <textarea
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            maxLength={100}
                            style={{ width: '100%', height: '60px', marginTop: '5px' }}
                            placeholder="Enter optional notes..."
                        />
                    </div>
                </Dialog>
            )}
        </>
    );
};

export default FeedActionButtons;
